/* stylelint-disable property-no-vendor-prefix */
import React, { FormEvent } from 'react'
import MaskedInput from 'react-text-mask'

import { compose, defaultProps } from 'recompose'
import styled from 'styled-components'

import LockPath from 'assets/icons/lock.svg'
import { TEXT_INPUT, TEXT_INPUT_ERROR } from 'utils/testing/testIDs'

import { theme } from '../../../theme'

export type Props = {
  autocomplete?: string
  autoFocus?: boolean
  className?: string
  disabled?: boolean
  error?: boolean
  errorText?: string
  id: string
  innerRef?: (input: HTMLInputElement) => void
  label?: string | JSX.Element
  mask?: Array<string | RegExp>
  name: string
  onChange?: (event: FormEvent<HTMLInputElement>) => void
  onFocus?: (event: FormEvent<HTMLInputElement>) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onBlur?: (event: FormEvent<HTMLInputElement>) => void
  type: 'text' | 'number' | 'password'
  placeholder?: string
  placeholderChar?: string
  required?: boolean
  value: string
  style?: any
  maxLength?: number
  helperText?: JSX.Element
  link?: string
  children?: any
  locked?: boolean | string
  charcounter?: boolean
  readOnly?: boolean
  prefix?: string
  hasHiddenLabel?: boolean
}

const Container = styled<any>('div')`
  display: block;
  user-select: none;
  position: relative;
  transition: opacity 150ms ease-in-out;

  /* Note: This gives us room to render an error label below the text field */
  padding-bottom: 30px;

  ${(props) =>
    props.disabled &&
    `
    opacity: 0.3;
  `};
`

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  color: ${theme('colors.slate')};
  margin-bottom: 8px;
`

const HiddenLabel = styled.label`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`

const ErrorLabel = styled(Label)`
  color: ${theme('colors.red')};
  font-size: 12px;
  font-weight: 300;
  margin-top: 3px;
  margin-bottom: 0;

  position: absolute;
  left: 0;
`

const Lock = styled.img`
  height: 14px;
  margin: 15px 16px;
  opacity: 0.3;
  position: absolute;
  right: 0;
`

const StyledInput = styled<any>('input')`
  height: 44px;
  border: 1px solid ${theme('colors.stoneLight')};
  padding: ${(props) =>
    props.maxLength && props.charcounter ? '0 44px 0 16px' : '0 16px'};
  border-radius: ${theme('styles.borderRadius')};
  font-size: 14px;
  color: ${theme('colors.slate')};
  width: 100%;
  transition: border-color 250ms ease-in-out;
  line-height: 1.5;

  -webkit-appearance: none;

  &::placeholder {
    color: ${theme('colors.stoneLight')};
  }

  &:focus,
  &:hover {
    outline: 0;
    border-color: ${theme('colors.stone')};
  }

  @media screen and (max-width: ${theme('breakpoints.sm')}px) {
    font-size: 16px;
  }

  ${(props) =>
    props['data-error'] &&
    `
      border-color: ${props.theme.colors.red};
      color: ${props.theme.colors.red};

      &:focus, &:hover {
        border-color: ${props.theme.colors.red};
      }
    `};

  ${(props) =>
    props.locked &&
    `
      color: ${props.theme.colors.stone};
      background-color: #fbfbfb;

      &:hover {
        border-color: ${props.theme.colors.stoneLight};
      }
    `};
`

const Prefix = styled.span`
  height: 14px;
  margin: 13px 5px;
  opacity: 0.5;
  position: absolute;
  left: 0;
`

const StyledMaskedInput = StyledInput.withComponent(MaskedInput)

const HelperText = styled<any>('div')`
  display: flex;
  width: 100%;
  font-size: 0.7rem;
  font-weight: 500;
  color: ${theme('colors.stone')};
  margin-top: ${(props) => (props.addExtraMarginTop ? 0 : '4px')};

  a {
    color: ${theme('colors.stone')};
    text-decoration: none;
  }
`

const CharsCounter = styled.div`
  color: ${theme('colors.slate')};
  font-size: 12px;
  line-height: 44px;
  margin-top: -44px;
  position: absolute;
  right: 20px;
`

// placeholderChar is NOT a space. It's â followed by 2 PAD characters.
// This is needed because if we have spaces in our value, we
// can't use a space as a placeholder character
const enhance = compose<Props, Props>(
  defaultProps({
    type: 'text',
    placeholderChar: ' ',
  }),
)

export default enhance((props: Props) => {
  const Input = props.mask ? StyledMaskedInput : StyledInput

  // This is for the prop disabled due to the new modifications made on props.locked to get rid of a console error
  let isLocked = null
  if (typeof props.locked === 'boolean') {
    isLocked = props.locked
  } else if (props.locked === 'true') {
    isLocked = true
  } else {
    isLocked = false
  }

  return (
    <Container
      className={props.className}
      disabled={props.disabled}
      style={props.style}
    >
      {props.label &&
        (!props.hasHiddenLabel ? (
          <Label htmlFor={props.id}>
            {props.label}
            {props.required && ' *'}
          </Label>
        ) : (
          <HiddenLabel htmlFor={props.id}>
            {props.label}
            {props.required && ' *'}
          </HiddenLabel>
        ))}
      {props.prefix && <Prefix>{props.prefix}</Prefix>}
      <Input
        autoComplete={props.autocomplete}
        autoFocus={props.autoFocus}
        data-error={props.error}
        data-testid={TEXT_INPUT}
        // CAUSES CONSOLE ERRORS, leaving as a comment in case removing this causes issues
        // defaultValue="" // Default value is needed for text masking to work on initial values; Edit: Seems to work without this
        disabled={props.disabled || isLocked}
        guide
        id={props.id}
        ref={props.innerRef}
        mask={props.mask}
        name={props.name}
        onBlur={props.onBlur}
        onChange={props.onChange}
        onFocus={props.onFocus}
        onKeyDown={props.onKeyDown}
        placeholder={props.placeholder}
        placeholderChar={props.placeholderChar}
        required={props.required}
        type={props.type}
        value={props.value || ''}
        maxLength={props.maxLength}
        charcounter={props.charcounter}
        readOnly={props.readOnly}
      />
      {props.locked && <Lock src={LockPath} />}
      {props.maxLength && props.charcounter && (
        <CharsCounter>
          {props.maxLength - (props.value || '').length}
        </CharsCounter>
      )}
      {props.helperText && (
        <HelperText addExtraMarginTop={props.errorText}>
          {props.link ? (
            <a href={props.link}>{props.helperText}</a>
          ) : props.name === 'newPassword' ? (
            !props.errorText && props.helperText
          ) : (
            props.helperText
          )}
        </HelperText>
      )}
      {props.error && props.errorText && (
        <ErrorLabel data-testid={TEXT_INPUT_ERROR}>
          {props.errorText}
        </ErrorLabel>
      )}
    </Container>
  )
}) as React.ComponentClass<Props>
